/**
 * @file PhotoTypeSlotList Organism Component
 * @description 사진 타입 슬롯 리스트 컴포넌트 (드래그 앤 드롭 지원)
 * @see docs/plans/loading/05-implementation.md - Section 36
 */

import React, { useCallback, memo, forwardRef } from 'react';
import styled from 'styled-components';
import colorSet from '@/src/styles/color';
import typo from '@/src/styles/typography';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import PhotoTypeSlotItem, {
  type PhotoTypeSlotData,
} from '@/src/components/molecule/PhotoTypeSlotItem';
import {
  DEFAULT_PHOTO_TYPE_TITLES,
  type PhotoTypeCode,
  type DefaultPhotoTypeCode,
} from '@/src/store/apis/photoType/interface';
import { generateNextCustomTypeCode } from '@/src/utils/photoType/validation';

export interface PhotoTypeSlotListProps {
  slots: PhotoTypeSlotData[];
  onSlotsChange: (slots: PhotoTypeSlotData[]) => void;
  onValidate?: (index: number, field: 'typeCode' | 'title' | 'subTitle', newValue?: string) => void;
  errors?: Record<
    string,
    { titleError?: string | null; subtitleError?: string | null; typeCodeError?: string | null }
  >;
  disabled?: boolean;
  autoFocusSlotId?: string | null;
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${colorSet.gray6};
  ${typo.b9r}
`;




/**
 * PhotoTypeSlotList - 사진 타입 슬롯 리스트
 *
 * @description
 * 드래그 앤 드롭으로 순서를 변경할 수 있는 사진 타입 슬롯 리스트입니다.
 * dnd-kit을 사용하여 키보드 및 포인터 접근성을 지원합니다.
 *
 * @example
 * ```tsx
 * const [slots, setSlots] = useState<PhotoTypeSlotData[]>(initialSlots);
 *
 * <PhotoTypeSlotList
 *   slots={slots}
 *   onSlotsChange={setSlots}
 *   errors={validationErrors}
 * />
 * ```
 */
const PhotoTypeSlotListComponent = forwardRef<HTMLDivElement, PhotoTypeSlotListProps>(({
  slots,
  onSlotsChange,
  onValidate,
  errors = {},
  disabled = false,
  autoFocusSlotId = null,
}, ref) => {
  const { t } = useTranslation(['photoType', 'common']);

  // 드래그 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 삭제 가능 여부 (항상 가능)
  const canDelete = true;

  /**
   * 드래그 종료 핸들러
   */
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = slots.findIndex((slot) => slot.id === active.id);
        const newIndex = slots.findIndex((slot) => slot.id === over.id);

        const newSlots = arrayMove(slots, oldIndex, newIndex).map(
          (slot, index) => ({
            ...slot,
            displayOrder: index + 1,
          })
        );

        onSlotsChange(newSlots);
      }
    },
    [slots, onSlotsChange]
  );

  const getDefaultTitle = useCallback((typeCode: PhotoTypeCode): string => {
    if (typeCode.startsWith('CUSTOM')) {
      return 'Custom';
    }
    return DEFAULT_PHOTO_TYPE_TITLES[typeCode as DefaultPhotoTypeCode] || typeCode;
  }, []);

  const handleTypeChange = useCallback(
    (index: number, typeCode: PhotoTypeCode) => {
      let finalTypeCode = typeCode;

      if (typeCode === 'CUSTOM') {
        const existingTypeCodes = slots.map((s) => s.typeCode);
        const nextCustomCode = generateNextCustomTypeCode(existingTypeCodes);

        if (!nextCustomCode) {
          return;
        }

        finalTypeCode = nextCustomCode as PhotoTypeCode;
      }

      const newSlots = slots.map((slot, i) =>
        i === index ? { ...slot, typeCode: finalTypeCode, title: getDefaultTitle(finalTypeCode) } : slot
      );
      onSlotsChange(newSlots);
    },
    [slots, onSlotsChange, getDefaultTitle]
  );

  /**
   * 제목 변경 핸들러
   */
  const handleTitleChange = useCallback(
    (index: number, title: string) => {
      const newSlots = slots.map((slot, i) =>
        i === index ? { ...slot, title } : slot
      );
      onSlotsChange(newSlots);
    },
    [slots, onSlotsChange]
  );

  /**
   * 부제목 변경 핸들러
   */
  const handleSubtitleChange = useCallback(
    (index: number, subtitle: string) => {
      const newSlots = slots.map((slot, i) =>
        i === index ? { ...slot, subTitle: subtitle || null } : slot
      );
      onSlotsChange(newSlots);
    },
    [slots, onSlotsChange]
  );

  /**
   * 삭제 핸들러
   */
  const handleDelete = useCallback(
    (index: number) => {
      if (!canDelete) return;

      const newSlots = slots
        .filter((_, i) => i !== index)
        .map((slot, i) => ({
          ...slot,
          displayOrder: i + 1,
        }));

      onSlotsChange(newSlots);
    },
    [slots, canDelete, onSlotsChange]
  );

  if (slots.length === 0) {
    return <EmptyMessage>{t('photoType:noSlots')}</EmptyMessage>;
  }

  return (
    <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={slots.map((slot) => slot.id)}
          strategy={verticalListSortingStrategy}
        >
          <ListContainer ref={ref}>
            {slots.map((slot, index) => (
              <PhotoTypeSlotItem
                key={slot.id}
                slot={slot}
                index={index}
                onTypeChange={handleTypeChange}
                onTitleChange={handleTitleChange}
                onSubtitleChange={handleSubtitleChange}
                onDelete={handleDelete}
                onValidate={onValidate}
                canDelete={canDelete}
                typeError={errors[slot.id]?.typeCodeError}
                titleError={errors[slot.id]?.titleError}
                subtitleError={errors[slot.id]?.subtitleError}
                disabled={disabled}
                autoFocus={slot.id === autoFocusSlotId}
              />
            ))}
          </ListContainer>
        </SortableContext>
      </DndContext>
  );
});

PhotoTypeSlotListComponent.displayName = 'PhotoTypeSlotList';

export const PhotoTypeSlotList = memo(PhotoTypeSlotListComponent);
export default PhotoTypeSlotList;
